<template>
  <div class="analysis-page">
    <!-- 헤더 영역 (필터 포함) -->
    <header class="page-header">
      <div class="header-left">
        <h1 class="page-title">
          <span class="icon">⛽</span>
          주유 통계 분석
        </h1>
      </div>

      <!-- 필터 영역 (헤더 내부) -->
      <div class="header-filters">
        <div class="filter-item">
          <input v-model="startDate" type="date" class="filter-input" />
        </div>
        <span class="filter-separator">~</span>
        <div class="filter-item">
          <input v-model="endDate" type="date" class="filter-input" />
        </div>
        <div class="filter-item">
          <select v-model="selectedEquipmentId" class="filter-select">
            <option value="">전체 장비</option>
            <option
              v-for="device in devices"
              :key="device.value"
              :value="device.value"
            >
              {{ device.label }}
            </option>
          </select>
        </div>
        <button class="filter-btn" :disabled="loading" @click="onApplyFilters">
          <span v-if="loading" class="spinner" />
          {{ loading ? "조회중..." : "🔍 조회" }}
        </button>
      </div>

      <nav class="header-nav">
        <button class="nav-btn" @click="goAnalysis">
          <span class="nav-icon">📊</span>
          운행 분석
        </button>
        <button class="nav-btn" @click="goBim">
          <span class="nav-icon">🏗️</span>
          진척도
        </button>
        <button class="nav-btn" @click="goMap">
          <span class="nav-icon">🗺️</span>
          지도
        </button>
        <button v-if="isLoggedIn" class="nav-btn" @click="openManual">
          <span class="nav-icon">📘</span>
          사용자 매뉴얼
        </button>
      </nav>
    </header>

    <!-- 에러 메시지 -->
    <div v-if="error" class="error-alert">
      <span class="error-icon">⚠️</span>
      {{ error }}
    </div>

    <!-- 메인 대시보드 -->
    <main class="dashboard-main">
      <!-- 대시보드 요약 카드 -->
      <div class="summary-row">
        <article class="stat-card wide">
          <div class="card-title-bar">
            <h2 class="card-title">
              <span class="title-icon">📊</span>
              전체 주유 대시보드
            </h2>
            <span class="card-badge">{{ dashboardData.period || "-" }}</span>
          </div>
          <div
            v-if="dashboardData.totalEquipmentCount !== undefined"
            class="dashboard-grid"
          >
            <div class="dashboard-item">
              <span class="dashboard-label">등록 장비</span>
              <span class="dashboard-value"
                >{{ formatNumber(dashboardData.totalEquipmentCount) }}대</span
              >
            </div>
            <div class="dashboard-item">
              <span class="dashboard-label">총 주유량</span>
              <span class="dashboard-value highlight"
                >{{ formatNumber(dashboardData.totalOilAmount, 1) }}L</span
              >
            </div>
            <div class="dashboard-item">
              <span class="dashboard-label">주유 횟수</span>
              <span class="dashboard-value"
                >{{ formatNumber(dashboardData.totalSupplyCount) }}회</span
              >
            </div>
            <div class="dashboard-item">
              <span class="dashboard-label">평균 단가</span>
              <span class="dashboard-value"
                >{{ formatCurrency(dashboardData.avgUnitPrice) }}/L</span
              >
            </div>
            <div class="dashboard-item">
              <span class="dashboard-label">유류대금</span>
              <span class="dashboard-value">{{
                formatCurrency(dashboardData.totalOilPrice)
              }}</span>
            </div>
            <div class="dashboard-item">
              <span class="dashboard-label">수수료</span>
              <span class="dashboard-value">{{
                formatCurrency(dashboardData.totalExtraPrice)
              }}</span>
            </div>
            <div class="dashboard-item">
              <span class="dashboard-label">용역비</span>
              <span class="dashboard-value">{{
                formatCurrency(dashboardData.totalServicePrice)
              }}</span>
            </div>
            <div class="dashboard-item">
              <span class="dashboard-label">총 비용</span>
              <span class="dashboard-value cost">{{
                formatCurrency(dashboardData.totalPrice)
              }}</span>
            </div>
          </div>
          <div v-else class="empty-state">
            <span class="empty-icon">📭</span>
            <p>데이터가 없습니다.</p>
          </div>
        </article>
      </div>

      <!-- 통계 카드 그리드 -->
      <div class="stats-grid">
        <!-- 장비별 주유 통계 요약 -->
        <article class="stat-card">
          <div class="card-title-bar">
            <h2 class="card-title">
              <span class="title-icon">🚜</span>
              장비별 주유 요약
            </h2>
          </div>
          <p class="card-desc">기간 내 장비별 총 주유량, 비용, 주유 횟수</p>
          <div v-if="summaryRows.length" class="data-table-wrap scrollable">
            <table class="data-table compact">
              <thead>
                <tr>
                  <th>장비명</th>
                  <th class="num">총주유량</th>
                  <th class="num">평균주유</th>
                  <th class="num">총비용</th>
                  <th class="num">주유횟수</th>
                  <th class="num">평균단가</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in summaryRows" :key="index">
                  <td class="equip-name">
                    {{ row.equipmentName || row.carNo || "-" }}
                  </td>
                  <td class="num highlight">
                    {{ formatNumber(row.totalOilAmount, 1) }}L
                  </td>
                  <td class="num">{{ formatNumber(row.avgOilAmount, 1) }}L</td>
                  <td class="num cost">{{ formatCurrency(row.totalPrice) }}</td>
                  <td class="num">{{ formatNumber(row.supplyCount) }}회</td>
                  <td class="num">{{ formatCurrency(row.avgUnitPrice) }}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="empty-state">
            <span class="empty-icon">📭</span>
            <p>데이터가 없습니다.</p>
          </div>
        </article>

        <!-- 비용 효율 분석 -->
        <article class="stat-card">
          <div class="card-title-bar">
            <h2 class="card-title">
              <span class="title-icon">💰</span>
              비용 효율 분석
            </h2>
            <span class="card-badge info">완료된 사이클만 집계</span>
          </div>
          <p class="card-desc">
            연비, km당 비용 등 운행 효율 지표 (다음 주유가 있는 건만 계산)
          </p>
          <div
            v-if="costEfficiencyRows.length"
            class="data-table-wrap scrollable"
          >
            <table class="data-table compact">
              <thead>
                <tr>
                  <th>장비명</th>
                  <th class="num">총주유량</th>
                  <th class="num">운행거리</th>
                  <th class="num">연비</th>
                  <th class="num">km당 비용</th>
                  <th class="num">회당 비용</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in costEfficiencyRows" :key="index">
                  <td class="equip-name">
                    {{ row.equipmentName || row.carNo || "-" }}
                  </td>
                  <td class="num">
                    {{ formatNumber(row.totalOilAmount, 1) }}L
                  </td>
                  <td class="num">
                    {{ formatNumber(row.totalDistanceKm, 1) }}km
                  </td>
                  <td class="num efficiency">
                    {{
                      row.fuelEfficiency
                        ? formatNumber(row.fuelEfficiency, 1) + " km/L"
                        : "-"
                    }}
                  </td>
                  <td class="num">
                    {{ row.costPerKm ? formatCurrency(row.costPerKm) : "-" }}
                  </td>
                  <td class="num cost">
                    {{ formatCurrency(row.costPerSupply) }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="empty-state">
            <span class="empty-icon">📭</span>
            <p>데이터가 없습니다.</p>
          </div>
        </article>
      </div>

      <!-- 유종별 통계 -->
      <div class="stats-grid">
        <article class="stat-card">
          <div class="card-title-bar">
            <h2 class="card-title">
              <span class="title-icon">🛢️</span>
              유종별 주유 통계
            </h2>
          </div>
          <p class="card-desc">유종(경유, 휘발유 등)별 주유 현황</p>
          <div v-if="oilTypeRows.length" class="data-table-wrap">
            <table class="data-table compact">
              <thead>
                <tr>
                  <th>유종</th>
                  <th class="num">총주유량</th>
                  <th class="num">총비용</th>
                  <th class="num">주유횟수</th>
                  <th class="num">평균단가</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(row, index) in oilTypeRows" :key="index">
                  <td class="equip-name">
                    <span
                      class="oil-type-badge"
                      :class="getOilTypeClass(row.oilType)"
                    >
                      {{ row.oilType || "-" }}
                    </span>
                  </td>
                  <td class="num highlight">
                    {{ formatNumber(row.totalOilAmount, 1) }}L
                  </td>
                  <td class="num cost">{{ formatCurrency(row.totalPrice) }}</td>
                  <td class="num">{{ formatNumber(row.supplyCount) }}회</td>
                  <td class="num">{{ formatCurrency(row.avgUnitPrice) }}/L</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div v-else class="empty-state">
            <span class="empty-icon">📭</span>
            <p>데이터가 없습니다.</p>
          </div>
        </article>

        <!-- 월별 주유 통계 -->
        <article class="stat-card">
          <div class="card-title-bar">
            <h2 class="card-title">
              <span class="title-icon">📅</span>
              월별 주유 추이
            </h2>
            <span class="card-badge secondary">{{ monthlyRows.length }}건</span>
          </div>
          <p class="card-desc">월별 주유량 및 비용 추이</p>
          <div v-if="monthlyRows.length" class="chart-container">
            <v-chart :option="monthlyChartOption" autoresize />
          </div>
          <div v-else class="empty-state">
            <span class="empty-icon">📭</span>
            <p>데이터가 없습니다.</p>
          </div>
        </article>
      </div>

      <!-- 일별 주유 통계 (전체 너비) -->
      <article class="stat-card full-width">
        <div class="card-title-bar">
          <h2 class="card-title">
            <span class="title-icon">📈</span>
            일별 주유 내역
          </h2>
          <span class="card-badge secondary">{{ dailyRows.length }}건</span>
        </div>
        <p class="card-desc">날짜별 주유 상세 내역</p>
        <div v-if="dailyRows.length" class="data-table-wrap scrollable">
          <table class="data-table">
            <thead>
              <tr>
                <th>주유일</th>
                <th>장비명</th>
                <th>차량번호</th>
                <th class="num">주유량</th>
                <th class="num">금액</th>
                <th class="num">주유횟수</th>
                <th class="num">평균단가</th>
                <th class="num">이동거리</th>
                <th class="num">연비</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(row, index) in dailyRows" :key="index">
                <td class="date-cell">{{ row.supplyDay }}</td>
                <td class="equip-name">{{ row.equipmentName || "-" }}</td>
                <td>{{ row.carNo || "-" }}</td>
                <td class="num highlight">
                  {{ formatNumber(row.totalOilAmount, 1) }}L
                </td>
                <td class="num cost">{{ formatCurrency(row.totalPrice) }}</td>
                <td class="num">{{ formatNumber(row.supplyCount) }}회</td>
                <td class="num">{{ formatCurrency(row.avgUnitPrice) }}</td>
                <td class="num">
                  {{
                    row.distanceToNextSupply != null
                      ? formatNumber(row.distanceToNextSupply, 2) + " km"
                      : "-"
                  }}
                </td>
                <td class="num efficiency">
                  {{
                    row.fuelEfficiency != null
                      ? formatNumber(row.fuelEfficiency, 2) + " km/L"
                      : "-"
                  }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div v-else class="empty-state">
          <span class="empty-icon">📭</span>
          <p>데이터가 없습니다.</p>
        </div>
      </article>
    </main>
  </div>
</template>

<script>
import OilSupplyStatisticsApi from "../api/osm/OilSupplyStatisticsApi";
import VChart from "vue-echarts";
import { use } from "echarts/core";
import { CanvasRenderer } from "echarts/renderers";
import { BarChart, LineChart } from "echarts/charts";
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
} from "echarts/components";

use([
  CanvasRenderer,
  BarChart,
  LineChart,
  GridComponent,
  TooltipComponent,
  LegendComponent,
]);

export default {
  name: "OilSupplyAnalysis",
  components: {
    VChart,
  },
  data() {
    return {
      startDate: this.getDefaultStartDate(),
      endDate: this.getDefaultEndDate(),
      selectedEquipmentId: "",
      devices: [],
      dashboardData: {},
      summaryRows: [],
      dailyRows: [],
      monthlyRows: [],
      costEfficiencyRows: [],
      oilTypeRows: [],
      loading: false,
      error: null,
      monthlyChartOption: null,
    };
  },
  computed: {
    bimId() {
      return parseInt(this.$route?.params?.id) || 1;
    },
    isLoggedIn() {
      return !!localStorage.getItem(JWT_TOKEN);
    },
  },
  async mounted() {
    await this.loadDevices();
    await this.onApplyFilters();
  },
  methods: {
    async loadDevices() {
      try {
        // 주유 통계 요약에서 장비 목록 가져오기
        const response = await OilSupplyStatisticsApi.getSummary({
          startDate: this.startDate,
          endDate: this.endDate,
        });
        const items = this.normalizeList(response);
        this.devices = items.map((item, index) => {
          const label =
            item.equipmentName ||
            item.carNo ||
            `장비-${item.equipmentId || index + 1}`;
          const value = item.equipmentId;
          return { label, value };
        });
      } catch (error) {
        console.error("장비 목록 불러오기 실패", error);
        this.devices = [];
      }
    },
    async onApplyFilters() {
      if (!this.startDate || !this.endDate) {
        this.error = "기간을 선택하세요.";
        return;
      }
      this.loading = true;
      this.error = null;
      try {
        await Promise.all([
          this.fetchDashboard(),
          this.fetchSummary(),
          this.fetchDaily(),
          this.fetchMonthly(),
          this.fetchCostEfficiency(),
          this.fetchByOilType(),
        ]);
      } catch (error) {
        console.error("주유 통계 데이터 조회 실패", error);
        this.error = "데이터를 불러오는 중 오류가 발생했습니다.";
      } finally {
        this.loading = false;
      }
    },
    async fetchDashboard() {
      const params = { startDate: this.startDate, endDate: this.endDate };
      const response = await OilSupplyStatisticsApi.getDashboard(params);
      this.dashboardData = response?.content || response || {};
    },
    async fetchSummary() {
      const params = { startDate: this.startDate, endDate: this.endDate };
      const response = this.selectedEquipmentId
        ? await OilSupplyStatisticsApi.getSummaryByEquipment(
            this.selectedEquipmentId,
            params
          )
        : await OilSupplyStatisticsApi.getSummary(params);
      this.summaryRows = this.normalizeList(response);
    },
    async fetchDaily() {
      const params = { startDate: this.startDate, endDate: this.endDate };
      const response = this.selectedEquipmentId
        ? await OilSupplyStatisticsApi.getDailyByEquipment(
            this.selectedEquipmentId,
            params
          )
        : await OilSupplyStatisticsApi.getDaily(params);
      this.dailyRows = this.normalizeList(response);
    },
    async fetchMonthly() {
      const params = { startDate: this.startDate, endDate: this.endDate };
      const response = this.selectedEquipmentId
        ? await OilSupplyStatisticsApi.getMonthlyByEquipment(
            this.selectedEquipmentId,
            params
          )
        : await OilSupplyStatisticsApi.getMonthly(params);
      this.monthlyRows = this.normalizeList(response);
      if (this.monthlyRows.length) {
        this.renderMonthlyChart();
      }
    },
    async fetchCostEfficiency() {
      const params = { startDate: this.startDate, endDate: this.endDate };
      const response = this.selectedEquipmentId
        ? await OilSupplyStatisticsApi.getCostEfficiencyByEquipment(
            this.selectedEquipmentId,
            params
          )
        : await OilSupplyStatisticsApi.getCostEfficiency(params);
      this.costEfficiencyRows = this.normalizeList(response);
    },
    async fetchByOilType() {
      const params = { startDate: this.startDate, endDate: this.endDate };
      const response = await OilSupplyStatisticsApi.getByOilType(params);
      this.oilTypeRows = this.normalizeList(response);
    },
    normalizeList(payload) {
      if (!payload) return [];
      if (Array.isArray(payload)) return payload;
      if (payload.content) {
        return Array.isArray(payload.content)
          ? payload.content
          : [payload.content];
      }
      if (payload.data) {
        return Array.isArray(payload.data) ? payload.data : [payload.data];
      }
      if (typeof payload === "object" && payload !== null) {
        return [payload];
      }
      return [];
    },
    renderMonthlyChart() {
      // 월별 데이터 집계 (장비별로 나뉘어 있을 수 있으므로)
      const monthlyMap = new Map();
      this.monthlyRows.forEach((row) => {
        const key = `${row.year}-${String(row.month).padStart(2, "0")}`;
        if (monthlyMap.has(key)) {
          const existing = monthlyMap.get(key);
          existing.totalOilAmount += row.totalOilAmount || 0;
          existing.totalPrice += row.totalPrice || 0;
        } else {
          monthlyMap.set(key, {
            month: key,
            totalOilAmount: row.totalOilAmount || 0,
            totalPrice: row.totalPrice || 0,
          });
        }
      });

      const sortedData = Array.from(monthlyMap.values()).sort((a, b) =>
        a.month.localeCompare(b.month)
      );

      const labels = sortedData.map((d) => d.month);
      const amounts = sortedData.map((d) => d.totalOilAmount.toFixed(1));
      const prices = sortedData.map((d) => (d.totalPrice / 10000).toFixed(0));

      this.monthlyChartOption = {
        tooltip: {
          trigger: "axis",
          backgroundColor: "rgba(15, 23, 42, 0.95)",
          borderColor: "rgba(255,255,255,0.1)",
          textStyle: { color: "#e2e8f0" },
        },
        legend: {
          data: ["주유량 (L)", "비용 (만원)"],
          textStyle: { color: "#94a3b8" },
          top: 0,
        },
        grid: {
          left: "3%",
          right: "4%",
          bottom: "3%",
          top: "15%",
          containLabel: true,
        },
        xAxis: {
          type: "category",
          data: labels,
          axisLine: { lineStyle: { color: "rgba(255,255,255,0.1)" } },
          axisLabel: { color: "#94a3b8", fontSize: 10 },
        },
        yAxis: [
          {
            type: "value",
            name: "주유량 (L)",
            axisLine: { lineStyle: { color: "rgba(255,255,255,0.1)" } },
            axisLabel: { color: "#94a3b8", fontSize: 10 },
            splitLine: { lineStyle: { color: "rgba(255,255,255,0.05)" } },
          },
          {
            type: "value",
            name: "비용 (만원)",
            axisLine: { lineStyle: { color: "rgba(255,255,255,0.1)" } },
            axisLabel: { color: "#94a3b8", fontSize: 10 },
            splitLine: { show: false },
          },
        ],
        series: [
          {
            name: "주유량 (L)",
            type: "bar",
            data: amounts,
            itemStyle: {
              color: "rgba(59, 130, 246, 0.8)",
              borderRadius: [4, 4, 0, 0],
            },
          },
          {
            name: "비용 (만원)",
            type: "line",
            yAxisIndex: 1,
            data: prices,
            itemStyle: { color: "#f59e0b" },
            lineStyle: { width: 2 },
            symbol: "circle",
            symbolSize: 6,
          },
        ],
      };
    },
    formatNumber(value, digits = 0) {
      if (value === null || value === undefined || isNaN(value)) return "-";
      return Number(value).toLocaleString(undefined, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      });
    },
    formatCurrency(value) {
      if (value === null || value === undefined || isNaN(value)) return "-";
      return Number(value).toLocaleString() + "원";
    },
    formatDistance(km) {
      if (km === null || km === undefined || isNaN(km)) return "-";
      return `${Number(km).toFixed(1)} km`;
    },
    getOilTypeClass(oilType) {
      if (!oilType) return "";
      if (oilType.includes("경유")) return "diesel";
      if (oilType.includes("휘발유")) return "gasoline";
      if (oilType.includes("LPG")) return "lpg";
      return "";
    },
    getDefaultStartDate() {
      const date = new Date();
      date.setMonth(date.getMonth() - 3);
      return this.formatDateInput(date);
    },
    getDefaultEndDate() {
      return this.formatDateInput(new Date());
    },
    formatDateInput(date) {
      const year = date.getFullYear();
      const month = `${date.getMonth() + 1}`.padStart(2, "0");
      const day = `${date.getDate()}`.padStart(2, "0");
      return `${year}-${month}-${day}`;
    },
    goAnalysis() {
      window.location.hash = `/dashboard/${this.bimId}/analysis`;
      window.location.reload();
    },
    goBim() {
      window.location.hash = `/dashboard/${this.bimId}`;
      window.location.reload();
    },
    goMap() {
      window.location.hash = `/dashboard/${this.bimId}/map`;
      window.location.reload();
    },
    openManual() {
      window.open("/manual/manual.html", "_blank");
    },
  },
};
</script>

<style scoped>
/* ===== 페이지 레이아웃 ===== */
.analysis-page {
  min-height: 100vh;
  padding: 32px 40px 60px;
  background: linear-gradient(135deg, #0a1628 0%, #132a3f 100%);
  color: #e2e8f0;
}

/* ===== 헤더 ===== */
.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 24px;
  margin-bottom: 28px;
  padding-bottom: 20px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  flex-wrap: wrap;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 6px;
}

.page-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 22px;
  font-weight: 800;
  color: #ffffff;
  letter-spacing: -0.5px;
  white-space: nowrap;
}

.page-title .icon {
  font-size: 24px;
}

/* ===== 헤더 내 필터 ===== */
.header-filters {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
  justify-content: center;
  flex-wrap: wrap;
}

.filter-separator {
  color: #64748b;
  font-weight: 500;
}

.header-filters .filter-item {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.header-filters .filter-input,
.header-filters .filter-select {
  padding: 8px 12px;
  background: rgba(0, 0, 0, 0.3);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 8px;
  color: #f1f5f9;
  font-size: 13px;
  transition: all 0.2s ease;
  min-width: 130px;
}

.header-filters .filter-input:focus,
.header-filters .filter-select:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.2);
}

.header-filters .filter-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 8px 16px;
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
  border: none;
  border-radius: 8px;
  color: #ffffff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.header-filters .filter-btn:hover:not(:disabled) {
  transform: translateY(-1px);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.35);
}

.header-filters .filter-btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.header-nav {
  display: flex;
  gap: 10px;
}

.nav-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: #e2e8f0;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  white-space: nowrap;
}

.nav-btn:hover {
  background: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.25);
  transform: translateY(-1px);
}

.nav-icon {
  font-size: 16px;
}

.spinner {
  width: 14px;
  height: 14px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-top-color: #ffffff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to {
    transform: rotate(360deg);
  }
}

/* ===== 에러 알림 ===== */
.error-alert {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 12px;
  color: #fca5a5;
  font-size: 14px;
  margin-bottom: 24px;
}

.error-icon {
  font-size: 20px;
}

/* ===== 대시보드 메인 ===== */
.dashboard-main {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

/* ===== 카드 그리드 ===== */
.summary-row {
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
}

.stats-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}

/* ===== 통계 카드 ===== */
.stat-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
  padding: 24px;
  transition: all 0.3s ease;
}

.stat-card:hover {
  background: rgba(255, 255, 255, 0.04);
  border-color: rgba(255, 255, 255, 0.12);
}

.stat-card.wide {
  grid-column: span 2;
}

.stat-card.full-width {
  width: 100%;
}

.card-title-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.card-title {
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0;
  font-size: 18px;
  font-weight: 700;
  color: #ffffff;
}

.title-icon {
  font-size: 22px;
}

.card-badge {
  padding: 4px 12px;
  background: linear-gradient(135deg, #10b981 0%, #059669 100%);
  border-radius: 20px;
  font-size: 11px;
  font-weight: 700;
  color: #ffffff;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.card-badge.secondary {
  background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
}

.card-badge.info {
  background: linear-gradient(135deg, #f59e0b 0%, #d97706 100%);
  font-size: 10px;
}

.card-desc {
  margin: 0 0 20px;
  font-size: 13px;
  color: #94a3b8;
}

/* ===== 대시보드 그리드 ===== */
.dashboard-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
}

.dashboard-item {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 16px;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.05);
}

.dashboard-label {
  font-size: 12px;
  color: #94a3b8;
  font-weight: 500;
}

.dashboard-value {
  font-size: 20px;
  font-weight: 700;
  color: #ffffff;
}

.dashboard-value.highlight {
  color: #60a5fa;
}

.dashboard-value.cost {
  color: #f59e0b;
}

.dashboard-value.efficiency {
  color: #34d399;
}

/* ===== 데이터 테이블 ===== */
.data-table-wrap {
  border-radius: 12px;
  overflow: hidden;
  overflow-x: auto;
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.data-table-wrap.scrollable {
  max-height: 350px;
  overflow-y: auto;
  overflow-x: auto;
}

.data-table {
  width: 100%;
  border-collapse: collapse;
}

.data-table thead {
  position: sticky;
  top: 0;
  z-index: 1;
}

.data-table th {
  padding: 14px 16px;
  background: rgba(0, 0, 0, 0.4);
  font-size: 12px;
  font-weight: 700;
  color: #94a3b8;
  text-align: center;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.data-table th.num {
  text-align: center;
}

.data-table td {
  padding: 14px 16px;
  font-size: 14px;
  color: #e2e8f0;
  text-align: center;
  border: none;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.data-table tbody tr {
  transition: background 0.2s ease;
}

.data-table tbody tr:hover {
  background: rgba(255, 255, 255, 0.03);
}

.data-table tbody tr:last-child td {
  border-bottom: none;
}

.data-table td.num {
  text-align: center;
  font-variant-numeric: tabular-nums;
}

.data-table td.highlight {
  color: #60a5fa;
  font-weight: 600;
}

.data-table td.cost {
  color: #f59e0b;
  font-weight: 600;
}

.data-table td.efficiency {
  color: #34d399;
  font-weight: 600;
}

.data-table td.equip-name {
  font-weight: 600;
  color: #ffffff;
}

.data-table td.date-cell {
  color: #94a3b8;
  font-weight: 500;
}

.data-table.compact th,
.data-table.compact td {
  padding: 12px 14px;
}

/* ===== 유종 배지 ===== */
.oil-type-badge {
  display: inline-block;
  padding: 4px 10px;
  border-radius: 6px;
  font-size: 12px;
  font-weight: 600;
}

.oil-type-badge.diesel {
  background: rgba(59, 130, 246, 0.2);
  color: #60a5fa;
}

.oil-type-badge.gasoline {
  background: rgba(239, 68, 68, 0.2);
  color: #f87171;
}

.oil-type-badge.lpg {
  background: rgba(34, 197, 94, 0.2);
  color: #4ade80;
}

/* ===== 빈 상태 ===== */
.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 20px;
  color: #64748b;
}

.empty-icon {
  font-size: 40px;
  margin-bottom: 12px;
  opacity: 0.5;
}

.empty-state p {
  margin: 0;
  font-size: 14px;
}

/* ===== 차트 컨테이너 ===== */
.chart-container {
  height: 280px;
}

/* ===== 반응형 ===== */
@media (max-width: 1200px) {
  .dashboard-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .stats-grid {
    grid-template-columns: 1fr;
  }

  .stat-card.wide {
    grid-column: span 1;
  }
}

@media (max-width: 768px) {
  .analysis-page {
    padding: 20px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
  }

  .header-filters {
    width: 100%;
    justify-content: flex-start;
  }

  .header-nav {
    width: 100%;
    justify-content: flex-end;
  }

  .dashboard-grid {
    grid-template-columns: 1fr;
  }
}
</style>
